import axios from "axios";

import {
  getAccessToken,
  getRefreshToken,
  setTokens,
  clearTokens,
} from "../utils/authStorage";

// =====================================================
// AXIOS INSTANCE
// =====================================================

const HotelApi = axios.create({
  baseURL: process.env.REACT_APP_BASE_URL,
  withCredentials: true,
  headers: {
    "Content-Type": "application/json",
  },
});

// Separate axios instance for refresh API
// This prevents refresh API from going through
// HotelApi's 401 interceptor.
const AuthApi = axios.create({
  baseURL: process.env.REACT_APP_BASE_URL,
  withCredentials: true,
  headers: {
    "Content-Type": "application/json",
  },
});

// =====================================================
// IMPORTANT
// Only ONE refresh request can run at a time.
// =====================================================

let refreshPromise = null;

// =====================================================
// FORCE LOGOUT
// =====================================================

const forceLogout = () => {
  clearTokens();

  // Prevent duplicate redirect
  if (window.location.pathname !== "/login") {
    window.location.replace("/login");
  }
};

// =====================================================
// REFRESH ACCESS TOKEN
// =====================================================

const refreshAccessToken = async () => {
  // ================================================
  // If refresh already running,
  // return same promise.
  // ================================================

  if (refreshPromise) {
    return refreshPromise;
  }

  const refreshToken = getRefreshToken();

  // ================================================
  // No refresh token
  // ================================================

  if (!refreshToken) {
    forceLogout();
  }

  // ================================================
  // Start refresh
  // ================================================

  refreshPromise = (async () => {
    try {
      const response = await AuthApi.post("/auth/refresh", {
        refreshToken,
      });

      const data = response.data;

      if (!data?.success) {
        throw new Error(data?.message || "Token refresh failed");
      }

      const newAccessToken = data?.data?.accessToken;

      const newRefreshToken = data?.data?.refreshToken;

      if (!newAccessToken) {
        throw new Error("Access token missing from refresh response");
      }

      // ==========================================
      // SAVE NEW TOKENS
      // ==========================================

      setTokens({
        accessToken: newAccessToken,

        refreshToken: newRefreshToken || refreshToken,
      });

      return newAccessToken;
    } catch (error) {
      // ==========================================
      // VERY IMPORTANT
      // ==========================================

      forceLogout();

      throw error;
    } finally {
      refreshPromise = null;
    }
  })();

  return refreshPromise;
};

// =====================================================
// REQUEST INTERCEPTOR
// =====================================================

HotelApi.interceptors.request.use(
  (config) => {
    const token = getAccessToken();

    config.headers = config.headers || {};

    // ==========================================
    // ATTACH ACCESS TOKEN
    // ==========================================

    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // ==========================================
    // LOCATION HEADERS
    // ==========================================

    if (typeof window !== "undefined") {
      config.headers["x-frontend-location"] = window.location.href;

      config.headers["x-backend-location"] = process.env.REACT_APP_BASE_URL;
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  },
);

// =====================================================
// RESPONSE INTERCEPTOR
// =====================================================

HotelApi.interceptors.response.use(
  // ==============================================
  // SUCCESS
  // ==============================================

  (response) => {
    return response;
  },

  // ==============================================
  // ERROR
  // ==============================================

  async (error) => {
    const originalRequest = error.config;

    const status = error.response?.status;

    // ==============================================
    // No request
    // ==============================================

    if (!originalRequest) {
      return Promise.reject(error);
    }

    // ==============================================
    // NEVER HANDLE REFRESH API HERE
    // ==============================================

    if (originalRequest.url?.includes("/auth/refresh")) {
      return Promise.reject(error);
    }

    // ==============================================
    // ONLY HANDLE 401
    // ==============================================

    if (status !== 401) {
      return Promise.reject(error);
    }

    // ==============================================
    // NO ACCESS TOKEN
    // DO NOT TRY REFRESH
    // ==============================================

    const accessToken = getAccessToken();

    if (!accessToken) {
      return Promise.reject(error);
    }

    // ==============================================
    // NO REFRESH TOKEN
    // DO NOT TRY REFRESH
    // ==============================================

    const refreshToken = getRefreshToken();

    if (!refreshToken) {
      return Promise.reject(error);
    }

    // ==============================================
    // REQUEST ALREADY RETRIED
    // ==============================================

    if (originalRequest._retry) {
      forceLogout();
      return Promise.reject(error);
    }

    // ==============================================
    // Mark request
    // ==============================================

    originalRequest._retry = true;

    try {
      // ==========================================
      // REFRESH TOKEN
      // ==========================================

      const newAccessToken = await refreshAccessToken();

      // ==========================================
      // ATTACH NEW TOKEN
      // ==========================================

      originalRequest.headers = originalRequest.headers || {};

      originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;

      // ==========================================
      // RETRY ORIGINAL REQUEST
      // ==========================================

      return HotelApi(originalRequest);
    } catch (refreshError) {
      forceLogout();

      return Promise.reject(refreshError);
    }
  },
);

// =====================================================
// COMMON REQUEST
// =====================================================

export const apiRequest = async (
  method,
  url,
  data = {},
  params = {},
  headers = {},
  signal,
) => {
  try {
    const response = await HotelApi({
      method,
      url,
      data,
      params,
      headers,
      signal,
    });

    return response.data;
  } catch (error) {
    // ==========================================
    // CANCELLED REQUEST
    // ==========================================

    if (axios.isCancel(error)) {
      return;
    }

    // ==========================================
    // CUSTOM ERROR
    // ==========================================

    const customError = new Error(
      error.response?.data?.message || error.message || "Request failed",
    );

    customError.success = false;

    customError.status = error.response?.status;

    customError.data = error.response?.data;

    throw customError;
  }
};

// =====================================================
// GET
// =====================================================

export const getData = (url, params = {}, headers = {}, signal) =>
  apiRequest("get", url, {}, params, headers, signal);

// =====================================================
// POST
// =====================================================

export const postData = (url, data = {}, headers = {}, signal) =>
  apiRequest("post", url, data, {}, headers, signal);

// =====================================================
// PUT
// =====================================================

export const putData = (url, data = {}, headers = {}, signal) =>
  apiRequest("put", url, data, {}, headers, signal);

// =====================================================
// PATCH
// =====================================================

export const patchData = (url, data = {}, headers = {}, signal) =>
  apiRequest("patch", url, data, {}, headers, signal);

// =====================================================
// DELETE
// =====================================================

export const deleteData = (url, params = {}, headers = {}, signal) =>
  apiRequest("delete", url, {}, params, headers, signal);

export default HotelApi;
